export enum FlowExecutionStatus {
  RUNNING = 'RUNNING',
  WAITING_FOR_USER = 'WAITING_FOR_USER',
  /**
   * An AGENT_TASK step called `wait_for_replies` (Slack conversation wait).
   * Distinct from WAITING_FOR_USER (ASK_USER/APPROVAL_GATE) so the ASK_USER/
   * APPROVAL_GATE resolvers and DoBox approval-entry listing — which gate on
   * WAITING_FOR_USER — never surface a reply-wait as something they can act on.
   * Resumed by handleConversationReply.ts, not the ASK_USER/APPROVAL_GATE resolvers.
   */
  WAITING_FOR_REPLY = 'WAITING_FOR_REPLY',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  REJECTED = 'REJECTED',
}

export interface FlowExecution {
  executionId: string;
  userId: string;
  flowId: string;
  status: FlowExecutionStatus;
  /** Index of the next step to execute (0-based). */
  cursor: number;
  /** Accumulated step outputs and trigger seed data. In-memory shape used by the
   * runner. Persisted in one of three forms depending on size: small contexts
   * gzipped inline as `variableContextGz`; large contexts offloaded to S3 with
   * only `variableContextS3Key` kept on the row (see FlowExecutionStore). The
   * store picks the form on write and reconstructs `variableContext` on read. */
  variableContext: Record<string, unknown>;
  /** Internal: gzip+base64 of `variableContext` when it fits inline under
   * DynamoDB's per-request limits. Written by FlowExecutionStore; inflated back
   * into `variableContext` by getExecution. Absent on rows written before
   * compression was introduced, and on rows offloaded to S3. */
  variableContextGz?: string;
  /** Internal: S3 object key holding the gzip+base64 `variableContext` when it
   * is too large to embed in a DynamoDB UpdateItem request (compression headroom
   * is finite — see issue #3190). Mutually exclusive with `variableContextGz`:
   * the store always sets exactly one and REMOVEs the other. getExecution only
   * fetches the object when the caller opts in (`hydrateContextFromS3`); the
   * resume resolver does not need the context and so never reads S3. */
  variableContextS3Key?: string;
  /** Internal: the stage (`prod`/`staging`) whose worker created the execution. Its
   *  offloaded context lives in that stage's bucket, so every continuation is
   *  enqueued onto that stage's queue. Absent on rows created before it was stamped. */
  ownerStage?: string;
  /** Internal: wrapped KMS data key sealing `variableContextGz` (inline or S3
   * body) under the AgentMemories CMK. Absent on rows written before context
   * encryption — those are read as legacy plaintext gzip until the backfill
   * runs. Never exposed in GraphQL. */
  encryptedDataKey?: string;
  /** Set when status is WAITING_FOR_USER — the agentId+order of the ASK_USER step. */
  pendingAskUserStepId?: string;
  /** Populated by setUserDecision; injected as the paused step's output on resume. */
  pendingDecision?: string;
  /**
   * The `timestampId` of the AgentExecutionHistory record created for the
   * initiating run.  Set when the execution first pauses at an ASK_USER step
   * so that the resume path can update the history record to SUCCESS/FAILURE.
   * Optional — absent on older executions and non-ASK_USER flows.
   */
  historyTimestampId?: string;
  /**
   * The conversationId of the per-execution system conversation created by the
   * ASK_USER strategy to hold the choice card for this execution.
   * Internal field — not exposed in GraphQL.
   * Absent on executions created before this field was added; the resolver
   * falls back to the shared background-tasks conversation in that case.
   */
  askUserConversationId?: string;
  /**
   * Checkpoint for a top-level FOR_EACH that yielded mid-loop to re-enqueue
   * itself (so a long loop is paced across invocations instead of blowing the
   * worker's 180s timeout). `stepOrder` is the FOR_EACH step's order and
   * `nextIteration` is the 0-based index of the next iteration to run. Present
   * only while a FOR_EACH is mid-pacing; cleared (REMOVEd) when any step
   * advances the cursor normally. See apps/api/ARCHITECTURE.md › FOR_EACH self-requeue.
   */
  forEachState?: {
    stepOrder: number;
    nextIteration: number;
  };
  startedAt: number;
  updatedAt: number;
  completedAt?: number;
  failureReason?: string;
  /** DynamoDB TTL epoch-seconds; set on terminal states for 30-day cleanup. */
  ttl?: number;
  /**
   * True when this execution is the one-time seed run fired by
   * `triggerInitialSetupExecution` for a template-created `isSetupOnly`
   * agent. Lets a caller answer "does this user have a setup run in flight"
   * without joining back to `UserCommunicationAgents`. Absent on every row
   * written before this field existed — always read as `false`, never as
   * unknown/blocking.
   */
  isSetupExecution?: boolean;
}

export enum ApprovalDecision {
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}
